import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'
import {
  TURN_FAILURE_MESSAGE_TYPE,
  conversationText,
  conversationCitations,
  type MessageResponse,
} from '@/api/types_session'
import type { CitationResponse } from '@/api/types'
import { FeedbackButtons } from './FeedbackButtons'
import { MarkdownContent } from './MarkdownContent'
import { TurnFailureCard } from './TurnFailureCard'
import { CollapsibleCitations } from './CollapsibleCitations'
import { TypingIndicator } from './TypingIndicator'
import { stripStreamingDirectiveTags } from '@/lib/fabric/stripDirectiveTags'
import { parseDelegationMessage } from '@/lib/delegationMarker'
import { DelegationMessageCard } from './DelegationMessageCard'
import { usePendingTurnsStore, hasPendingTurnForMessage } from '@/stores/pendingTurns'

interface MessageBubbleProps {
  message: MessageResponse
  isLatest?: boolean
  onCitationClick?: (citation: CitationResponse) => void
  onRetry?: () => void
  isRetrying?: boolean
}

/**
 * One message of the transcript — a session record from tricorder-session:
 * role (user / agent), the conversation payload's text and citations, or a
 * durable turn_failure record. The card surfaces of the old wire (widget
 * blocks, confidence, colleague suggestions, images, two-tier summaries,
 * administration records in the transcript) died with the
 * conversation domain's move — a message's payload is text / tool / citation
 * blocks, and the session's records live on its record stream, not here.
 */
export const MessageBubble = memo(function MessageBubble({
  message,
  isLatest,
  onCitationClick,
  onRetry,
  isRetrying = false,
}: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const text = conversationText(message.payload)
  const citations = conversationCitations(message.payload)
  const delegation = useMemo(() => {
    if (!isUser) return null
    const parsed = parseDelegationMessage(text)
    if (!parsed) return null
    return parsed.agent === message.author.performer ? parsed : null
  }, [isUser, text, message.author.performer])

  // In-flight async-turn placeholder: useStreamingChat inserts an empty agent
  // bubble keyed by the pending turn's message id; the answer streams in as
  // turn.delta events appended to this placeholder and lands as the persisted
  // message on turn.completed.
  const isPendingTurn = usePendingTurnsStore((s) =>
    !isUser && hasPendingTurnForMessage(s.turns, message.id),
  )

  // A durable terminal failure: its reason, options and actions (TurnFailureCard).
  if (message.type === TURN_FAILURE_MESSAGE_TYPE) {
    return (
      <TurnFailureCard
        payload={message.payload}
        agentSlug={message.author.performer}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    )
  }

  // A delegation message is a user-role record the child's AGENT performed —
  // one agent's question to another, or the answer coming back. The envelope
  // is text anyone can type, so the performer's agent claim is checked
  // against the envelope's (SPEC.md section 7).

  if (delegation) {
    return (
      <DelegationMessageCard
        delegation={delegation}
        isLatest={isLatest}
      />
    )
  }

  // While the turn is pending, the placeholder's text is the RAW delta stream:
  // fabric directive tags reach it before the extractor lifts them, so strip
  // them here as the deltas append — the persisted message lands already
  // stripped. A settled message, and anything a nurse typed, renders verbatim.
  const visibleText =
    isPendingTurn && !isUser ? stripStreamingDirectiveTags(text) : text

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div
          className={cn(
            'max-w-[85%] rounded-2xl rounded-br-sm px-4 py-3',
            'bg-primary text-primary-foreground whitespace-pre-wrap break-words text-[15px] leading-relaxed',
          )}
        >
          {visibleText}
        </div>
      </div>
    )
  }

  if (isPendingTurn && !visibleText) {
    return <TypingIndicator />
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] min-w-0">
        <div className="rounded-2xl rounded-bl-sm bg-white px-4 py-3 shadow-sm border border-neutral-100">
          <MarkdownContent content={visibleText} />
          {citations.length > 0 && (
            <CollapsibleCitations messageId={message.id} citations={citations} onCitationClick={onCitationClick} />
          )}
          <FeedbackButtons message={message} />
        </div>
      </div>
    </div>
  )
})
